import { Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, Routes, RouterLink, RouterOutlet } from '@angular/router';
import { MegaMenuItem, MenuItem } from 'primeng/api';
import Keycloak from 'keycloak-js';
import { filter } from 'rxjs';
import { MegaMenu } from 'primeng/megamenu';
import { Button } from 'primeng/button';
import { Avatar } from 'primeng/avatar';
import { Breadcrumb } from 'primeng/breadcrumb';
import { Divider } from 'primeng/divider';

@Component({
    selector: 'app-frame',
    templateUrl: './frame.page.html',
    styleUrl: './frame.page.scss',
    imports: [RouterLink, MegaMenu, Button, Avatar, Breadcrumb, Divider, RouterOutlet]
})
export class FramePage implements OnDestroy, OnInit
{
    private activatedRouter = inject(ActivatedRoute);
    private router = inject(Router);
    private keycloak = inject(Keycloak);
    private destroyRef = inject(DestroyRef);
    public rotas: Routes | undefined = this.activatedRouter.routeConfig!.children;

    public items: MenuItem[] | undefined;
    public home: MenuItem | undefined;
    public megaMenuItems: MegaMenuItem[] = [];
    public showPage = signal(true);
    private pageAnimationTimer?: ReturnType<typeof setTimeout>;
    private previousUrl = '';

    public ngOnInit(): void
    {
        this.home = { icon: 'pi pi-home', routerLink: '/' };
        this.previousUrl = this.router.url;
        this.buildBreadcrumb();
        this.buildMegaMenu();

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(event =>
        {
            this.buildBreadcrumb();
            if (this.shouldAnimatePageTransition(event.urlAfterRedirects))
                this.animatePageTransition();

            this.previousUrl = event.urlAfterRedirects;
        });
    }

    public logout(): void
    {
        this.keycloak.logout({ redirectUri: window.location.origin });
    }

    public ngOnDestroy(): void
    {
        clearTimeout(this.pageAnimationTimer);
    }

    private animatePageTransition(): void
    {
        this.showPage.set(false);
        clearTimeout(this.pageAnimationTimer);
        this.pageAnimationTimer = setTimeout(() =>
        {
            this.showPage.set(true);
        }, 250);
    }

    private shouldAnimatePageTransition(currentUrl: string): boolean
    {
        const tabbedRoutes = ['/admin/plans/', '/admin/candidates/', '/admin/goals/'];

        const navigatingBetweenTabs = tabbedRoutes.some(route =>
            this.previousUrl.startsWith(route) && currentUrl.startsWith(route));

        return !navigatingBetweenTabs;
    }

    private buildMegaMenu(): void
    {
        this.megaMenuItems = (this.rotas ?? [])
            .filter(rota => rota.data && !rota.data['skip'])
            .map(rota =>
            {
                const submenuItems: MenuItem[] = (rota.data!['menu'] ?? []).map((child: { path: string; icon: string; text: string }) => ({
                    label: child.text,
                    icon: child.icon,
                    routerLink: [`/${rota.path}/${child.path}`]
                }));

                return {
                    label: rota.data!['text'],
                    icon: rota.data!['icon'],
                    items: submenuItems.length ? [[{ items: submenuItems }]] : undefined
                };
            });
    }

    private buildBreadcrumb() : void
    {
        const breadcrumbs: MenuItem[] = [];

        this.activatedRouter.root.children.forEach(route =>
        {
            this.processRoute(route.snapshot, breadcrumbs);
        });

        this.items = breadcrumbs;
    }

    private processRoute(route: ActivatedRouteSnapshot, breadcrumbs: MenuItem[]): void
    {
        const data = route.data;

        if (data && data['text'] && !data['skip'])
        {
            const path = this.getCleanPath(route);
            if (!breadcrumbs.find(bc => bc.label === data['text']))
            {
                breadcrumbs.push({
                    label: data['text'],
                    routerLink: [path]
                });
            }
        }

        route.children.forEach(childRoute =>
        {
            if (childRoute.outlet === 'primary')
                this.processRoute(childRoute, breadcrumbs);
        });
    }

    private getCleanPath(route: ActivatedRouteSnapshot): string
    {
        return route.url.map(segment => segment.toString()).join('/');
    }
}